import { useComputed, useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { ClientError, navigateTo, RedirectError } from "bunfx";
import { useEffect, useRef } from "preact/hooks";
import {
  IcoCheck,
  IcoChevronLeft,
  IcoChevronRight,
  IcoPencil,
  IcoPlus,
  IcoSearch,
  IcoSpinner,
  IcoX,
} from "../../../components/icons";
import {
  BrandedLayout,
  CommentButton,
  CommentThread,
  MentionRichTextEditor,
  PriorityInput,
  PriorityList,
  PrioritySection,
  RatingInput,
  ReactionBar,
} from "../../../components/ui";
import { Confetti } from "../../../components/ui/confetti";
import {
  RatingTrendChart,
  type RatingTrendPoint,
} from "../../../components/ui/rating-sparkline";
import { decodeMentionsToHtml } from "../../../components/ui/rich-text-mention-extension";
import { usePageTitle } from "../../../hooks";
import { rpc } from "../../../rpc";
import { showError } from "../../../stores/errors";
import type { MimicState } from "../../../stores/mimic";
import {
  type BrandColor,
  type CoreValue,
  type FormAnswer,
  isAdmin,
  type Priority,
  type QuestionType,
  type ReactionSummary,
  type User,
  type ValueRecognition,
} from "../../../types";
import { formatDateWithTime, formatPeriodRange } from "../../../utils/format";
import { linkifyHtml } from "../../../utils/linkify";

type FormInfo = {
  id: string;
  title: string;
  orgId: string;
  orgName: string;
  orgLogoUrl: string | null;
  periodStart: Date | null;
  periodEnd: Date | null;
  prioritiesEnabled: boolean;
  valuesRecognitionEnabled: boolean;
};

type QuestionInfo = {
  id: string;
  title: string;
  description: string;
  type: QuestionType;
  ratingMax: number;
  seq: number;
  required: boolean;
  allowMultiple: boolean;
};

type SubmissionInfo = {
  id: string;
  periodStart: Date;
  periodEnd: Date | null;
  submittedAt: Date | null;
  memberName: string | null;
  memberProfilePictureUrl: string | null;
  memberId: string | null;
  answers: FormAnswer[];
  reviewedAt: Date | null;
  reviewedBy: string | null;
  canReview: boolean;
  /** Manager's review state (shown to admins viewing someone else's report) */
  managerReviewedAt?: Date | null;
  managerReviewedBy?: string | null;
};

type PrioritiesState = {
  currentPeriod: Priority[];
  previousPeriod: Priority[];
  currentPeriodStart: Date | null;
  previousPeriodStart: Date | null;
};

type SubmitFormState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  brandColor: BrandColor;
  hasReports: boolean;
  isOrgAdmin: boolean;
  currentMemberId: string | null;
  form: FormInfo;
  questions: QuestionInfo[];
  submission: SubmissionInfo | null;
  isEditable: boolean;
  viewingPastSubmission: boolean;
  priorities: PrioritiesState | null;
  answerCommentCounts: Record<string, number>;
  priorityCommentCounts: Record<string, number>;
  answerReactions: Record<string, ReactionSummary[]>;
  priorityReactions: Record<string, ReactionSummary[]>;
  coreValues: CoreValue[];
  recognitions: ValueRecognition[];
  recognitionCommentCounts: Record<string, number>;
  recognitionReactions: Record<string, ReactionSummary[]>;
  notAvailable?: boolean;
  viewMode: "full" | "mention_only";
  highlightAnswerId?: string;
  highlightPriorityId?: string;
  highlightRecognitionId?: string;
  ratingTrends?: Record<string, RatingTrendPoint[]>;
  isGracePeriod?: boolean;
  gracePeriodStart?: Date | null;
  gracePeriodEnd?: Date | null;
};

export async function load(args: LoaderArgs): Promise<SubmitFormState> {
  const orgId = args.params.orgId ?? "";
  const formId = args.params.formId ?? "";
  // Support both path param (/submissions/:id) and query param (?submission=)
  const submissionId =
    args.params.submissionId ??
    args.url.searchParams.get("submission") ??
    undefined;
  const uuidRe =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const rawAnswer = args.url.searchParams.get("answer");
  const rawPriority = args.url.searchParams.get("priority");
  const highlightAnswerId =
    rawAnswer && uuidRe.test(rawAnswer) ? rawAnswer : undefined;
  const highlightPriorityId =
    rawPriority && uuidRe.test(rawPriority) ? rawPriority : undefined;
  const rawRecognition = args.url.searchParams.get("recognition");
  const highlightRecognitionId =
    rawRecognition && uuidRe.test(rawRecognition) ? rawRecognition : undefined;

  // Run all three calls in parallel — use allSettled so we can still show
  // the "not available" page even if the form call fails.
  const [authSettled, orgsSettled, formSettled] = await Promise.allSettled([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.submissions.getFormForFilling({ formId, submissionId }),
  ]);

  // Auth is required — redirect if missing
  if (authSettled.status === "rejected") {
    throw new RedirectError("/login");
  }
  const authResult = authSettled.value;
  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const orgsResult =
    orgsSettled.status === "fulfilled"
      ? orgsSettled.value
      : { organizations: [] };

  // Handle form fetch failure
  if (formSettled.status === "rejected") {
    const err = formSettled.reason;
    if (err instanceof ClientError) {
      if (err.code === "form_not_found" || err.code === "not_assigned") {
        const orgFromList = orgsResult.organizations.find(
          (o) => o.id === orgId,
        );
        const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;
        return {
          currentUser: authResult.user as User,
          mimic: authResult.mimic ?? null,
          orgId,
          brandColor,
          hasReports: orgFromList?.hasReports ?? false,
          isOrgAdmin: orgFromList ? isAdmin(orgFromList.role) : false,
          currentMemberId: null,
          form: {
            id: "",
            title: "",
            orgId,
            orgName: orgFromList?.name ?? "",
            orgLogoUrl: null,
            periodStart: null,
            periodEnd: null,
            prioritiesEnabled: false,
            valuesRecognitionEnabled: false,
          },
          questions: [],
          submission: null,
          isEditable: false,
          viewingPastSubmission: false,
          priorities: null,
          answerCommentCounts: {},
          priorityCommentCounts: {},
          answerReactions: {},
          priorityReactions: {},
          coreValues: [],
          recognitions: [],
          recognitionCommentCounts: {},
          recognitionReactions: {},
          notAvailable: true,
          viewMode: "full",
        };
      }
    }
    throw err;
  }

  const result = formSettled.value;

  // Get brand color from list endpoint
  const orgFromList = orgsResult.organizations.find(
    (o) => o.id === result.form.orgId,
  );
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  // Verify form belongs to the org in the URL
  if (result.form.orgId !== orgId) {
    throw new RedirectError(`/orgs/${result.form.orgId}/forms/${formId}`);
  }

  // Fetch priorities + answer batch data in parallel (phase 1)
  const batch: {
    priorities: PrioritiesState | null;
    answerCommentCounts: Record<string, number>;
    answerReactions: Record<string, ReactionSummary[]>;
  } = { priorities: null, answerCommentCounts: {}, answerReactions: {} };

  // Use grace-shifted period so priorities/drafts align with the submission's actual period
  const periodStart =
    result.submission?.periodStart ??
    result.gracePeriodStart ??
    result.form.periodStart;
  const answerIds = (result.submission?.answers ?? [])
    .filter((a) => a.id)
    .map((a) => a.id as string);

  const phase1: Promise<void>[] = [];

  // Priorities fetch
  if (result.form.prioritiesEnabled && periodStart) {
    const currentPeriodStart = new Date(periodStart);
    // Use server-computed previous period start (handles initial partial periods
    // and schedule changes correctly). Null means no previous period exists.
    const prevDate = result.form.previousPeriodStart
      ? new Date(result.form.previousPeriodStart)
      : null;

    phase1.push(
      (async () => {
        const targetMemberId = result.submission?.memberId;
        if (targetMemberId) {
          const [currentResult, previousResult] = await Promise.all([
            rpc.priorities
              .getMemberPriorities({
                workspaceId: orgId,
                memberId: targetMemberId,
                periodStart: currentPeriodStart,
              })
              .catch((err) => {
                console.error(
                  "Failed to load member priorities (current):",
                  err,
                );
                return { priorities: [], memberName: "" };
              }),
            prevDate
              ? rpc.priorities
                  .getMemberPriorities({
                    workspaceId: orgId,
                    memberId: targetMemberId,
                    periodStart: prevDate,
                  })
                  .catch((err) => {
                    console.error(
                      "Failed to load member priorities (previous):",
                      err,
                    );
                    return { priorities: [], memberName: "" };
                  })
              : Promise.resolve({ priorities: [], memberName: "" }),
          ]);
          batch.priorities = {
            currentPeriod: currentResult.priorities,
            previousPeriod: previousResult.priorities,
            currentPeriodStart,
            previousPeriodStart: prevDate ?? currentPeriodStart,
          };
        } else {
          // When no previous period, pass currentPeriodStart as a no-op placeholder
          // (it will match current period which we already have, and we discard the result)
          const effectivePrevDate = prevDate ?? currentPeriodStart;
          const prioritiesResult = await rpc.priorities
            .getPriorities({
              workspaceId: orgId,
              currentPeriodStart,
              previousPeriodStart: effectivePrevDate,
            })
            .catch((err) => {
              console.error("Failed to load priorities:", err);
              return { currentPeriod: [], previousPeriod: [] };
            });
          batch.priorities = {
            currentPeriod: prioritiesResult.currentPeriod,
            previousPeriod: prevDate ? prioritiesResult.previousPeriod : [],
            currentPeriodStart,
            previousPeriodStart: effectivePrevDate,
          };
        }
      })(),
    );
  }

  // Rating trend data (when reviewing someone else's submission)
  let ratingTrends: Record<string, RatingTrendPoint[]> = {};
  const isViewingOther =
    result.submission?.memberId &&
    result.submission.memberId !== result.currentMemberId;
  if (isViewingOther) {
    const ratingQuestionIds = result.questions
      .filter((q) => q.type === "rating")
      .map((q) => q.id);
    if (ratingQuestionIds.length > 0 && result.submission?.memberId) {
      phase1.push(
        rpc.submissions
          .getRatingTrends({
            workspaceId: orgId,
            formId,
            memberIds: [result.submission.memberId],
            periodCount: 8,
            upToPeriod: new Date(result.submission.periodStart).toISOString(),
          })
          .then((r) => {
            const memberId = result.submission!.memberId!;
            if (r.trends[memberId]) {
              ratingTrends = r.trends[memberId] ?? {};
            }
          })
          .catch((err) => {
            console.error("Failed to load rating trends:", err);
          }),
      );
    }
  }

  // Answer batch data (comment counts + reactions)
  if (answerIds.length > 0 && result.currentMemberId) {
    phase1.push(
      rpc.comments
        .getCommentCounts({
          workspaceId: orgId,
          targetType: "form_answer",
          targetIds: answerIds,
        })
        .then((r) => {
          batch.answerCommentCounts = r.counts;
        })
        .catch((err) => {
          console.error("Failed to load answer comment counts:", err);
        }),
    );
    phase1.push(
      rpc.reactions
        .getReactionSummaries({
          workspaceId: orgId,
          targetType: "form_answer",
          targetIds: answerIds,
        })
        .then((r) => {
          batch.answerReactions = r.summaries;
        })
        .catch((err) => {
          console.error("Failed to load answer reactions:", err);
        }),
    );
  }

  await Promise.all(phase1);

  // Phase 2: priority batch data (needs priority IDs from phase 1)
  let priorityCommentCounts: Record<string, number> = {};
  let priorityReactions: Record<string, ReactionSummary[]> = {};

  if (batch.priorities && result.currentMemberId) {
    const allPriorityIds = [
      ...batch.priorities.previousPeriod.map((p: Priority) => p.id),
      ...batch.priorities.currentPeriod.map((p: Priority) => p.id),
    ];

    if (allPriorityIds.length > 0) {
      const [pcCounts, pcReactions] = await Promise.all([
        rpc.comments
          .getCommentCounts({
            workspaceId: orgId,
            targetType: "priority",
            targetIds: allPriorityIds,
          })
          .catch((err) => {
            console.error("Failed to load priority comment counts:", err);
            return { counts: {} as Record<string, number> };
          }),
        rpc.reactions
          .getReactionSummaries({
            workspaceId: orgId,
            targetType: "priority",
            targetIds: allPriorityIds,
          })
          .catch((err) => {
            console.error("Failed to load priority reactions:", err);
            return { summaries: {} } as {
              summaries: Record<string, ReactionSummary[]>;
            };
          }),
      ]);
      priorityCommentCounts = pcCounts.counts;
      priorityReactions = pcReactions.summaries;
    }
  }

  // Phase 3: Load values recognition data if enabled
  let coreValues: CoreValue[] = [];
  let recognitions: ValueRecognition[] = [];
  let recognitionCommentCounts: Record<string, number> = {};
  let recognitionReactions: Record<string, ReactionSummary[]> = {};

  if (result.form.valuesRecognitionEnabled && result.currentMemberId) {
    // Use grace-shifted period so recognitions align with the submission's actual period
    const periodStart =
      result.submission?.periodStart ??
      result.gracePeriodStart ??
      result.form.periodStart;

    if (periodStart) {
      // Determine which member's recognitions to load
      const authorMemberId =
        result.submission?.memberId ?? result.currentMemberId;

      const [cvResult, recResult] = await Promise.all([
        rpc.coreValues
          .list({ workspaceId: orgId })
          .catch(() => ({ values: [] as CoreValue[] })),
        authorMemberId
          ? rpc.valueRecognitions
              .listByAuthor({
                workspaceId: orgId,
                authorMemberId,
                periodStart: new Date(periodStart),
              })
              .catch(() => ({ recognitions: [] as ValueRecognition[] }))
          : Promise.resolve({ recognitions: [] as ValueRecognition[] }),
      ]);

      coreValues = cvResult.values;
      recognitions = recResult.recognitions;

      // Batch load comment counts and reactions for recognitions
      const recIds = recognitions.map((r) => r.id);
      if (recIds.length > 0) {
        const [rcCounts, rcReactions] = await Promise.all([
          rpc.comments
            .getCommentCounts({
              workspaceId: orgId,
              targetType: "value_recognition",
              targetIds: recIds,
            })
            .catch(() => ({ counts: {} as Record<string, number> })),
          rpc.reactions
            .getReactionSummaries({
              workspaceId: orgId,
              targetType: "value_recognition",
              targetIds: recIds,
            })
            .catch(() => ({
              summaries: {} as Record<string, ReactionSummary[]>,
            })),
        ]);
        recognitionCommentCounts = rcCounts.counts;
        recognitionReactions = rcReactions.summaries;
      }
    }
  }

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    orgId,
    brandColor,
    hasReports: orgFromList?.hasReports ?? false,
    isOrgAdmin: orgFromList ? isAdmin(orgFromList.role) : false,
    currentMemberId: result.currentMemberId,
    form: result.form,
    questions: result.questions,
    submission: result.submission,
    isEditable: result.isEditable,
    viewingPastSubmission: !!submissionId,
    priorities: batch.priorities,
    answerCommentCounts: batch.answerCommentCounts,
    priorityCommentCounts,
    answerReactions: batch.answerReactions,
    priorityReactions,
    coreValues,
    recognitions,
    recognitionCommentCounts,
    recognitionReactions,
    ratingTrends,
    viewMode: result.viewMode ?? "full",
    highlightAnswerId,
    highlightPriorityId,
    highlightRecognitionId,
    isGracePeriod: result.isGracePeriod ?? false,
    gracePeriodStart: result.gracePeriodStart ?? null,
    gracePeriodEnd: result.gracePeriodEnd ?? null,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle(state.value.form.title);
  const {
    currentUser,
    mimic,
    orgId,
    brandColor,
    hasReports,
    isOrgAdmin,
    currentMemberId,
    form,
    questions,
    submission,
    isEditable,
    viewingPastSubmission,
    priorities: initialPriorities,
    answerCommentCounts: initialAnswerCommentCounts,
    priorityCommentCounts: initialPriorityCommentCounts,
    answerReactions: initialAnswerReactions,
    priorityReactions: initialPriorityReactions,
    coreValues,
    recognitions: initialRecognitions,
    recognitionCommentCounts: initialRecognitionCommentCounts,
    recognitionReactions: initialRecognitionReactions,
    ratingTrends,
    notAvailable,
    viewMode,
    highlightAnswerId,
    highlightPriorityId,
    highlightRecognitionId,
    isGracePeriod,
    gracePeriodStart,
    gracePeriodEnd,
  } = state.value;

  if (notAvailable) {
    return (
      <BrandedLayout
        user={currentUser}
        mimic={mimic}
        orgId={orgId}
        orgName={form.orgName}
        brandColor={brandColor}
        hasReports={hasReports}
        activeTab="forms"
        isAdmin={isOrgAdmin}
      >
        <div class="mx-auto max-w-xl px-4 py-16 text-center">
          <div class="text-4xl mb-4">&#128196;</div>
          <h1 class="text-xl font-semibold text-[var(--text)]">
            Form not available
          </h1>
          <p class="mt-2 text-[var(--text-muted)]">
            This form isn't assigned to your account ({currentUser.email}). If
            you received a link for a different account, try signing in with
            that email instead.
          </p>
          <div class="mt-6 flex flex-col items-center gap-3">
            <a
              href={`/orgs/${orgId}`}
              class="inline-block rounded-lg bg-[var(--brand-600)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--brand-700)] transition-colors"
            >
              Go to dashboard
            </a>
            <a
              href="/login"
              class="text-sm font-medium text-[var(--brand-600)] hover:text-[var(--brand-700)] transition-colors"
            >
              Sign in with a different account
            </a>
          </div>
        </div>
      </BrandedLayout>
    );
  }

  // Initialize answers from existing submission (using signal to avoid stale closure)
  // For multi-answer questions, first answer goes to primary, rest to extra
  const multiQuestionIds = new Set(
    questions.filter((q) => q.allowMultiple).map((q) => q.id),
  );

  const buildInitialAnswers = (): {
    primary: Record<string, FormAnswer>;
    extra: Record<string, FormAnswer[]>;
  } => {
    const primary: Record<string, FormAnswer> = {};
    const extra: Record<string, FormAnswer[]> = {};
    if (submission) {
      // Group answers by questionId
      const grouped: Record<string, FormAnswer[]> = {};
      for (const answer of submission.answers) {
        const list = grouped[answer.questionId] ?? [];
        list.push(answer);
        grouped[answer.questionId] = list;
      }
      for (const [qid, groupedAnswers] of Object.entries(grouped)) {
        const first = groupedAnswers[0];
        if (first) {
          primary[qid] = first;
          if (groupedAnswers.length > 1 && multiQuestionIds.has(qid)) {
            extra[qid] = groupedAnswers.slice(1);
          }
        }
      }
    }
    return { primary, extra };
  };

  const initialState = buildInitialAnswers();

  // --- Draft auto-save: restore from localStorage for unsubmitted forms ---
  const draftPeriodStart =
    submission?.periodStart ?? gracePeriodStart ?? form.periodStart;
  const draftKey =
    draftPeriodStart && currentMemberId
      ? `pulse_draft_${form.id}_${currentMemberId}_${new Date(draftPeriodStart).toISOString()}`
      : null;

  let restoredPrimary = initialState.primary;
  let restoredExtra = initialState.extra;
  if (draftKey && isEditable && !submission?.submittedAt) {
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.primary && typeof draft.primary === "object") {
          restoredPrimary = draft.primary;
        }
        if (draft.extra && typeof draft.extra === "object") {
          restoredExtra = draft.extra;
        }
      }
    } catch {
      // Ignore corrupt drafts
    }
  }

  const initialAnswers = useSignal<Record<string, FormAnswer>>(
    initialState.primary,
  );
  const answers = useSignal<Record<string, FormAnswer>>(restoredPrimary);

  // Extra answers for multi-answer questions (beyond the primary)
  const initialExtraAnswers = useSignal<Record<string, FormAnswer[]>>(
    initialState.extra,
  );
  const extraAnswers = useSignal<Record<string, FormAnswer[]>>(restoredExtra);

  const submitting = useSignal(false);
  const justSubmitted = useSignal(false);
  const reviewedAt = useSignal<Date | null>(submission?.reviewedAt ?? null);
  const markingReviewed = useSignal(false);
  const allReviewedDone = useSignal(false);

  // Priority state — useSignal only initializes on mount; sync from loader data
  // on re-renders (e.g., client-side navigation without remount).
  const previousPriorities = useSignal<Priority[]>(
    initialPriorities?.previousPeriod ?? [],
  );
  const currentPriorities = useSignal<Priority[]>(
    initialPriorities?.currentPeriod ?? [],
  );
  const hasPriorities = initialPriorities !== null;

  // Sync priorities when loader data changes (handles re-navigation without remount)
  useEffect(() => {
    previousPriorities.value = initialPriorities?.previousPeriod ?? [];
    currentPriorities.value = initialPriorities?.currentPeriod ?? [];
  }, [initialPriorities]);

  // Track unsaved priority input text so we can auto-save on form submit
  const pendingPriorityText = useSignal("");

  // Build set of previous priority IDs that were carried to current period
  // (for showing "Carried to this week" annotation in manager view)
  const carriedFromIds = useComputed(() => {
    const ids = new Set<string>();
    for (const p of currentPriorities.value) {
      if (p.carriedFromId) {
        ids.add(p.carriedFromId);
      }
    }
    return ids;
  });

  // Comment counts (keyed by target ID) — pre-loaded from loader
  const answerCommentCounts = useSignal<Record<string, number>>(
    initialAnswerCommentCounts,
  );
  const priorityCommentCounts = useSignal<Record<string, number>>(
    initialPriorityCommentCounts,
  );

  // Recognition state
  const recognitionsList = useSignal<ValueRecognition[]>(initialRecognitions);
  const recognitionCommentCounts = useSignal<Record<string, number>>(
    initialRecognitionCommentCounts,
  );
  const recognitionReactions = useSignal<Record<string, ReactionSummary[]>>(
    initialRecognitionReactions,
  );
  const showRecognitionForm = useSignal(false);
  const recognitionSaving = useSignal(false);

  // Sync recognitions when loader data changes
  useEffect(() => {
    recognitionsList.value = initialRecognitions;
    recognitionCommentCounts.value = initialRecognitionCommentCounts;
    recognitionReactions.value = initialRecognitionReactions;
  }, [
    initialRecognitions,
    initialRecognitionCommentCounts,
    initialRecognitionReactions,
  ]);

  // Check if there are unsaved changes
  const hasChanges = useComputed(() => {
    const current = answers.value;
    const initial = initialAnswers.value;

    // Check if any primary answers differ from initial
    for (const questionId of questions.map((q) => q.id)) {
      const currentAnswer = current[questionId];
      const initialAnswer = initial[questionId];

      // Both empty - no change
      if (!currentAnswer && !initialAnswer) continue;

      // One exists, other doesn't - change
      if (!currentAnswer || !initialAnswer) return true;

      // Compare values
      if (currentAnswer.textAnswer !== initialAnswer.textAnswer) return true;
      if (currentAnswer.ratingAnswer !== initialAnswer.ratingAnswer)
        return true;
    }

    // Check extra answers for multi-answer questions
    const currentExtra = extraAnswers.value;
    const initialExtra = initialExtraAnswers.value;
    for (const qid of multiQuestionIds) {
      const curr = currentExtra[qid] ?? [];
      const init = initialExtra[qid] ?? [];
      if (curr.length !== init.length) return true;
      for (let i = 0; i < curr.length; i++) {
        const c = curr[i];
        const n = init[i];
        if (!c || !n) return true;
        if (c.textAnswer !== n.textAnswer) return true;
        if (c.ratingAnswer !== n.ratingAnswer) return true;
      }
    }

    return false;
  });

  // Warn before navigating away with unsaved changes.
  // Note: Empty deps array is intentional. With Preact signals, accessing .value
  // inside the handler is reactive - the handler will see the current value when
  // called. We only need to set up the listener once on mount.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges.value && isEditable && !justSubmitted.value) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Auto-save draft to localStorage (debounced 500ms)
  const draftTimerRef = useRef<number>(0);
  useEffect(() => {
    if (
      !draftKey ||
      !isEditable ||
      !!submission?.submittedAt ||
      justSubmitted.value
    )
      return;
    clearTimeout(draftTimerRef.current);
    draftTimerRef.current = window.setTimeout(() => {
      try {
        const primary = answers.value;
        const extra = extraAnswers.value;
        const hasContent =
          Object.values(primary).some(
            (a) =>
              (a.textAnswer ?? "").replace(/<[^>]*>/g, "").trim() ||
              a.ratingAnswer != null,
          ) ||
          Object.values(extra).some((arr) =>
            arr.some(
              (a) =>
                (a.textAnswer ?? "").replace(/<[^>]*>/g, "").trim() ||
                a.ratingAnswer != null,
            ),
          );
        if (hasContent) {
          localStorage.setItem(draftKey, JSON.stringify({ primary, extra }));
        } else {
          localStorage.removeItem(draftKey);
        }
      } catch {
        // localStorage may be full or disabled
      }
    }, 500);
    return () => clearTimeout(draftTimerRef.current);
  }, [answers.value, extraAnswers.value]);

  const updateAnswer = (questionId: string, answer: FormAnswer) => {
    answers.value = { ...answers.value, [questionId]: answer };
  };

  const addExtraAnswer = (questionId: string) => {
    const current = extraAnswers.value[questionId] ?? [];
    if (current.length >= 10) return; // Cap at 10 extra answers
    extraAnswers.value = {
      ...extraAnswers.value,
      [questionId]: [
        ...current,
        {
          id: crypto.randomUUID(), // Stable key for rendering
          questionId,
          textAnswer: "",
          ratingAnswer: null,
        },
      ],
    };
  };

  const updateExtraAnswer = (
    questionId: string,
    index: number,
    answer: FormAnswer,
  ) => {
    const current = [...(extraAnswers.value[questionId] ?? [])];
    current[index] = answer;
    extraAnswers.value = { ...extraAnswers.value, [questionId]: current };
  };

  const removeExtraAnswer = (questionId: string, index: number) => {
    const current = [...(extraAnswers.value[questionId] ?? [])];
    current.splice(index, 1);
    extraAnswers.value = { ...extraAnswers.value, [questionId]: current };
  };

  // Priority handlers
  const handleTogglePriorityComplete = async (
    id: string,
    completed: boolean,
    isPrevious: boolean,
  ) => {
    try {
      await rpc.priorities.updatePriority({ priorityId: id, completed });
      // Update local state
      const updateList = isPrevious ? previousPriorities : currentPriorities;
      updateList.value = updateList.value.map((p) =>
        p.id === id ? { ...p, completedAt: completed ? new Date() : null } : p,
      );
    } catch (err) {
      showError(
        "Failed to update priority",
        err instanceof Error ? err.message : "Please try again.",
      );
    }
  };

  const handleUpdatePriorityTitle = async (id: string, title: string) => {
    try {
      await rpc.priorities.updatePriority({ priorityId: id, title });
      // Update local state
      currentPriorities.value = currentPriorities.value.map((p) =>
        p.id === id ? { ...p, title } : p,
      );
    } catch (err) {
      showError(
        "Failed to update priority",
        err instanceof Error ? err.message : "Please try again.",
      );
    }
  };

  const handleDeletePriority = async (id: string) => {
    try {
      await rpc.priorities.deletePriority({ priorityId: id });
      currentPriorities.value = currentPriorities.value.filter(
        (p) => p.id !== id,
      );
    } catch (err) {
      showError(
        "Failed to delete priority",
        err instanceof Error ? err.message : "Please try again.",
      );
    }
  };

  const handleAddPriority = async (title: string) => {
    if (!initialPriorities?.currentPeriodStart) return;
    try {
      const result = await rpc.priorities.createPriority({
        workspaceId: orgId,
        periodStart: initialPriorities.currentPeriodStart,
        title,
      });
      // Capture focus state before signal update triggers parent re-render
      const priorityInput = document.querySelector<HTMLInputElement>(
        'input[name="priority"]',
      );
      const hadFocus = document.activeElement === priorityInput;
      currentPriorities.value = [...currentPriorities.value, result.priority];
      // Restore focus if the user was typing when the async update landed
      if (hadFocus && priorityInput) {
        requestAnimationFrame(() => priorityInput.focus());
      }
    } catch (err) {
      showError(
        "Failed to add priority",
        err instanceof Error ? err.message : "Please try again.",
      );
    }
  };

  const handleReorderPriorities = async (orderedIds: string[]) => {
    if (!initialPriorities?.currentPeriodStart) return;
    // Save previous state for rollback
    const previousOrder = currentPriorities.value;

    // Optimistically update local state
    const ordered = orderedIds
      .map((id) => currentPriorities.value.find((p) => p.id === id))
      .filter((p): p is Priority => p !== undefined);
    currentPriorities.value = ordered;

    try {
      await rpc.priorities.reorderPriorities({
        workspaceId: orgId,
        periodStart: initialPriorities.currentPeriodStart,
        orderedIds,
      });
    } catch (err) {
      // Rollback on failure
      currentPriorities.value = previousOrder;
      showError(
        "Failed to reorder priorities",
        err instanceof Error ? err.message : "Please try again.",
      );
    }
  };

  const handleCarryForward = async (priorityId: string) => {
    if (!initialPriorities?.currentPeriodStart) return;

    try {
      const result = await rpc.priorities.carryForward({
        priorityId,
        workspaceId: orgId,
        targetPeriodStart: initialPriorities.currentPeriodStart,
      });
      // Add the carried priority to current period list
      currentPriorities.value = [...currentPriorities.value, result.priority];
    } catch (err) {
      if (err instanceof ClientError && err.code === "already_carried") {
        // Already carried - this is fine, the UI will show "Already carried"
        return;
      }
      showError(
        "Failed to carry forward",
        err instanceof Error ? err.message : "Please try again.",
      );
    }
  };

  // Recognition handlers
  const handleCreateRecognition = async (
    recipientMemberIds: string[],
    valueIds: string[],
    message: string,
  ) => {
    // Use grace-shifted period so recognitions align with the submission's period
    const periodStart =
      submission?.periodStart ?? gracePeriodStart ?? form.periodStart;
    if (!periodStart) return;

    recognitionSaving.value = true;
    try {
      await rpc.valueRecognitions.create({
        workspaceId: orgId,
        message,
        periodStart: new Date(periodStart),
        recipientMemberIds,
        valueIds,
      });

      // Reload recognitions to get full data with recipients/values
      const authorMemberId = submission?.memberId ?? currentMemberId;
      if (authorMemberId) {
        const updated = await rpc.valueRecognitions.listByAuthor({
          workspaceId: orgId,
          authorMemberId,
          periodStart: new Date(periodStart),
        });
        recognitionsList.value = updated.recognitions;
      }

      showRecognitionForm.value = false;
    } catch (err) {
      showError(
        "Failed to save recognition",
        err instanceof Error ? err.message : "Please try again.",
      );
    } finally {
      recognitionSaving.value = false;
    }
  };

  const handleRemoveRecognition = async (id: string) => {
    const prev = recognitionsList.value;
    recognitionsList.value = prev.filter((r) => r.id !== id);
    try {
      await rpc.valueRecognitions.remove({ id });
    } catch (err) {
      recognitionsList.value = prev;
      showError(
        "Failed to remove recognition",
        err instanceof Error ? err.message : "Please try again.",
      );
    }
  };

  const handleEditRecognition = async (
    id: string,
    recipientMemberIds: string[],
    valueIds: string[],
    message: string,
  ) => {
    try {
      await rpc.valueRecognitions.update({
        id,
        recipientMemberIds,
        valueIds,
        message,
      });

      // Reload recognitions to get fresh data
      const periodStart =
        submission?.periodStart ?? gracePeriodStart ?? form.periodStart;
      const authorMemberId = submission?.memberId ?? currentMemberId;
      if (authorMemberId && periodStart) {
        const updated = await rpc.valueRecognitions.listByAuthor({
          workspaceId: orgId,
          authorMemberId,
          periodStart: new Date(periodStart),
        });
        recognitionsList.value = updated.recognitions;
      }
    } catch (err) {
      showError(
        "Failed to update recognition",
        err instanceof Error ? err.message : "Please try again.",
      );
      throw err; // Re-throw so the card knows the save failed
    }
  };

  const handleSubmit = async () => {
    if (submitting.value || !isEditable) return;

    // Validate required questions
    const missing = questions.filter((q) => {
      if (!q.required) return false;
      const answer = answers.value[q.id];
      if (!answer) return true;
      if (q.type === "short_answer") {
        const text = (answer.textAnswer ?? "")
          .replace(/<[^>]*>/g, "")
          .replace(/&nbsp;/g, " ")
          .trim();
        return !text;
      }
      return answer.ratingAnswer == null;
    });

    if (missing.length > 0) {
      showError(
        "Required questions",
        "Please answer all required questions before submitting.",
      );
      return;
    }

    // Require at least one recognition when values recognition is enabled
    if (
      form.valuesRecognitionEnabled &&
      coreValues.length > 0 &&
      recognitionsList.value.length === 0
    ) {
      showError(
        "Recognition required",
        "Please recognize at least one teammate before submitting.",
      );
      return;
    }

    submitting.value = true;

    try {
      // Auto-save any unsaved priority text before submitting
      const pendingText = pendingPriorityText.value.trim();
      if (pendingText && initialPriorities?.currentPeriodStart) {
        try {
          const result = await rpc.priorities.createPriority({
            workspaceId: orgId,
            periodStart: initialPriorities.currentPeriodStart,
            title: pendingText,
          });
          currentPriorities.value = [
            ...currentPriorities.value,
            result.priority,
          ];
          pendingPriorityText.value = "";
        } catch (err) {
          console.error("Failed to auto-save priority:", err);
        }
      }

      // Combine primary answers + extra answers for multi-answer questions
      const answerList = Object.values(answers.value);
      for (const extras of Object.values(extraAnswers.value)) {
        for (const extra of extras) {
          // Only include non-empty extra answers
          const text = (extra.textAnswer ?? "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/g, " ")
            .trim();
          if (text || extra.ratingAnswer != null) {
            // Strip client-side temp id before sending to server
            const { id: _, ...answer } = extra;
            answerList.push(answer as FormAnswer);
          }
        }
      }
      await rpc.submissions.submit({
        formId: form.id,
        answers: answerList,
      });
      justSubmitted.value = true;
      // Clear draft from localStorage on successful submit
      if (draftKey) {
        try {
          localStorage.removeItem(draftKey);
        } catch {}
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      showError(
        "Failed to submit form",
        err instanceof Error ? err.message : "Please try again.",
      );
      submitting.value = false;
    }
  };

  const handleMarkReviewed = async (andNext = false) => {
    if (!submission || markingReviewed.value) return;
    markingReviewed.value = true;

    try {
      if (reviewedAt.value && !andNext) {
        // Unmark as reviewed
        await rpc.submissions.unmarkReviewed({ submissionId: submission.id });
        reviewedAt.value = null;
      } else {
        // Mark as reviewed
        await rpc.submissions.markReviewed({ submissionId: submission.id });
        reviewedAt.value = new Date();

        // If "review & next", find and navigate to next unreviewed
        if (andNext) {
          // Read scope from URL so admin-managers reviewing with scope=all
          // don't get silently scoped to just their direct reports
          const urlScope = new URL(window.location.href).searchParams.get(
            "scope",
          );
          const nextResult = await rpc.submissions.getNextUnreviewed({
            workspaceId: orgId,
            currentSubmissionId: submission.id,
            periodStart: new Date(submission.periodStart).toISOString(),
            scope:
              urlScope === "all" || urlScope === "team" ? urlScope : undefined,
          });

          if (nextResult.nextSubmission) {
            // Navigate to next unreviewed submission, preserving scope
            const scopeParam = urlScope ? `?scope=${urlScope}` : "";
            navigateTo(
              `/orgs/${orgId}/forms/${nextResult.nextSubmission.formId}/submissions/${nextResult.nextSubmission.id}${scopeParam}`,
            );
            return;
          }
          // No more unreviewed - show "all done" state
          allReviewedDone.value = true;
        }
      }
    } catch (err) {
      showError(
        "Failed to update review status",
        err instanceof Error ? err.message : "Please try again.",
      );
    } finally {
      markingReviewed.value = false;
    }
  };

  const isNotificationView =
    !!highlightAnswerId || !!highlightPriorityId || !!highlightRecognitionId;
  const effectiveEditable =
    isEditable && !justSubmitted.value && !isNotificationView;

  const statusMessage = getFormStatusMessage({
    submission,
    isEditable,
    viewingPastSubmission,
    isMimicking: !!mimic,
    mimicName: mimic?.memberName ?? null,
  });

  // Determine if viewing someone else's submission
  const isViewingOthersSubmission =
    submission !== null &&
    submission.memberName !== null &&
    submission.memberName.length > 0;
  const isMentionOnly = viewMode === "mention_only";

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={form.orgName}
      brandColor={brandColor}
      activeTab={
        isMentionOnly ? "home" : isViewingOthersSubmission ? "team" : "home"
      }
      isAdmin={isOrgAdmin}
      hasReports={hasReports}
    >
      <div class="flex justify-center pb-24">
        <div class="w-full max-w-2xl flex flex-col gap-10">
          {/* Success banner or back link */}
          {justSubmitted.value ? (
            <>
              <SuccessBanner />
              <a
                href={`/orgs/${orgId}`}
                class="inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-medium border border-border text-text hover:bg-surface-hover transition-colors"
              >
                Return to Home
              </a>
            </>
          ) : (
            <>
              {/* Back link */}
              <a
                href={
                  isMentionOnly || !isViewingOthersSubmission
                    ? `/orgs/${orgId}`
                    : `/orgs/${orgId}/team`
                }
                class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
              >
                <IcoChevronLeft class="-ml-[5px] size-4" />
                {isMentionOnly || !isViewingOthersSubmission
                  ? "Back to Home"
                  : "Back to Team"}
              </a>

              {/* Header */}
              {isViewingOthersSubmission ? (
                // Viewing someone else's submission - 15five style
                <header class="flex items-start justify-between gap-4 pb-4 border-b border-border">
                  <div class="flex items-center gap-4">
                    {submission!.memberProfilePictureUrl ? (
                      <img
                        src={submission!.memberProfilePictureUrl}
                        alt=""
                        class="size-14 rounded-full object-cover"
                      />
                    ) : (
                      <div class="flex items-center justify-center size-14 rounded-full bg-[var(--brand-100)] dark:bg-[var(--brand-900)]/30 text-[var(--brand-600)] dark:text-[var(--brand-400)] text-xl font-semibold">
                        {submission!.memberName!.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h1 class="text-xl font-semibold text-text">
                        {submission!.memberName}
                      </h1>
                      <p class="text-sm text-text-muted">
                        {form.title || "Check-in"}
                      </p>
                      {submission!.periodStart && (
                        <p class="text-xs text-text-subtle">
                          {formatPeriodRange(
                            submission!.periodStart,
                            submission!.periodEnd,
                          )}
                        </p>
                      )}
                      {isMentionOnly && (
                        <p class="text-xs text-text-subtle mt-1">
                          You were mentioned in{" "}
                          {questions.length === 1
                            ? "this response"
                            : "these responses"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div class="text-right text-sm">
                    {submission!.submittedAt && (
                      <p class="text-text-muted">
                        Submitted on{" "}
                        <span class="text-text font-medium">
                          {formatDateWithTime(submission!.submittedAt)}
                        </span>
                      </p>
                    )}
                    {!isMentionOnly &&
                      (reviewedAt.value || submission!.managerReviewedBy) && (
                        <div class="mt-1 space-y-0.5">
                          {reviewedAt.value && (
                            <p class="text-green-700 dark:text-green-400 text-sm flex items-center gap-1">
                              <IcoCheck class="size-3.5" />
                              Reviewed by you
                            </p>
                          )}
                          {submission!.managerReviewedBy && (
                            <p class="text-text-muted text-sm flex items-center gap-1">
                              <IcoCheck class="size-3.5" />
                              Reviewed by {submission!.managerReviewedBy}
                            </p>
                          )}
                        </div>
                      )}
                  </div>
                </header>
              ) : (
                // Own submission or filling form
                <header class="flex items-start justify-between gap-4 pb-4 border-b border-border">
                  <div class="flex items-center gap-4">
                    {currentUser.profilePictureUrl ? (
                      <img
                        src={currentUser.profilePictureUrl}
                        alt=""
                        class="size-14 rounded-full object-cover"
                      />
                    ) : (
                      <div class="flex items-center justify-center size-14 rounded-full bg-[var(--brand-100)] dark:bg-[var(--brand-900)]/30 text-[var(--brand-600)] dark:text-[var(--brand-400)] text-xl font-semibold">
                        {(currentUser.name || currentUser.email || "?")
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h1 class="text-xl font-semibold text-text">
                        {currentUser.name || currentUser.email}
                      </h1>
                      <p class="text-sm text-text-muted">
                        Your {form.title || "Check-in"}
                      </p>
                      {(submission?.periodStart ||
                        gracePeriodStart ||
                        form.periodStart) && (
                        <p class="text-xs text-text-subtle">
                          {formatPeriodRange(
                            submission?.periodStart ??
                              gracePeriodStart ??
                              form.periodStart,
                            submission?.periodEnd ??
                              gracePeriodEnd ??
                              form.periodEnd,
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <div class="text-right text-sm">
                    {isGracePeriod &&
                      effectiveEditable &&
                      !submission?.submittedAt && (
                        <div class="mb-1">
                          <span class="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide">
                            Late
                          </span>
                        </div>
                      )}
                    {viewingPastSubmission && submission?.submittedAt && (
                      <p class="text-text-muted">
                        Submitted on{" "}
                        <span class="text-text font-medium">
                          {formatDateWithTime(submission.submittedAt)}
                        </span>
                      </p>
                    )}
                    {effectiveEditable && !submission?.submittedAt && (
                      <p class="text-text-muted">
                        {isGracePeriod
                          ? "You can still submit for last week"
                          : "Take a few minutes to reflect on your week"}
                      </p>
                    )}
                    {effectiveEditable &&
                      (isGracePeriod && gracePeriodEnd ? (
                        <p class="text-text-muted mt-1">
                          Closes{" "}
                          {formatDateWithTime(
                            new Date(
                              new Date(gracePeriodEnd).getTime() +
                                24 * 60 * 60 * 1000,
                            ),
                          )}
                        </p>
                      ) : form.periodEnd ? (
                        <p class="text-text-muted mt-1">
                          Due {formatDateWithTime(form.periodEnd)}
                        </p>
                      ) : null)}
                    {statusMessage && statusMessage.type !== "success" && (
                      <p
                        class={`text-sm mt-1 ${
                          statusMessage.type === "warning"
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-text-muted"
                        }`}
                      >
                        {statusMessage.text}
                      </p>
                    )}
                  </div>
                </header>
              )}
            </>
          )}

          {/* Split questions: rating first, then text questions after priorities */}
          {(() => {
            const ratingQuestions = questions.filter(
              (q) => q.type === "rating",
            );
            const textQuestions = questions.filter((q) => q.type !== "rating");

            const renderQuestionCard = (
              question: (typeof questions)[0],
              editable: boolean,
            ) => {
              const answer = answers.value[question.id] ?? null;
              const extras = question.allowMultiple
                ? editable
                  ? (extraAnswers.value[question.id] ?? [])
                  : (extraAnswers.value[question.id] ?? [])
                : [];
              if (editable) {
                return (
                  <QuestionCard
                    key={question.id}
                    question={question}
                    answer={answer}
                    onAnswer={(a) => updateAnswer(question.id, a)}
                    disabled={false}
                    workspaceId={orgId}
                    currentMemberId={currentMemberId ?? undefined}
                    commentCount={0}
                    isMimicking={false}
                    extraAnswers={extras}
                    onAddExtraAnswer={
                      question.allowMultiple
                        ? () => addExtraAnswer(question.id)
                        : undefined
                    }
                    onUpdateExtraAnswer={
                      question.allowMultiple
                        ? (i, a) => updateExtraAnswer(question.id, i, a)
                        : undefined
                    }
                    onRemoveExtraAnswer={
                      question.allowMultiple
                        ? (i) => removeExtraAnswer(question.id, i)
                        : undefined
                    }
                  />
                );
              }
              return (
                <div key={question.id} class="py-6 first:pt-0 last:pb-0">
                  <QuestionCard
                    question={question}
                    answer={answer}
                    onAnswer={(a) => updateAnswer(question.id, a)}
                    disabled={true}
                    workspaceId={orgId}
                    currentMemberId={currentMemberId ?? undefined}
                    commentCount={
                      answer?.id
                        ? (answerCommentCounts.value[answer.id] ?? 0)
                        : 0
                    }
                    initialReactions={
                      answer?.id ? initialAnswerReactions[answer.id] : undefined
                    }
                    isMimicking={!!mimic}
                    extraAnswers={extras}
                    extraAnswerReactions={initialAnswerReactions}
                    ratingTrend={
                      question.type === "rating"
                        ? ratingTrends?.[question.id]
                        : undefined
                    }
                    autoExpandComments={
                      !!highlightAnswerId && answer?.id === highlightAnswerId
                    }
                  />
                </div>
              );
            };

            return (
              <>
                {questions.length === 0 ? (
                  <div class="py-12 text-center">
                    <p class="text-text-muted">
                      This form has no questions yet.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Priority sections - at the top */}
                    {hasPriorities && (
                      <>
                        {previousPriorities.value.length > 0 && (
                          <PrioritySection
                            variant="previous"
                            title={
                              isViewingOthersSubmission
                                ? `${submission!.memberName}'s Previous Priorities`
                                : "Priorities from Last Check-in"
                            }
                            description={
                              effectiveEditable
                                ? "Mark priorities from your past check-in as complete"
                                : isViewingOthersSubmission
                                  ? "What they planned to accomplish"
                                  : "Priorities carried from the previous period"
                            }
                          >
                            <PriorityList
                              priorities={previousPriorities.value}
                              isFromPrevious={true}
                              onToggleComplete={
                                effectiveEditable
                                  ? (id, completed) =>
                                      handleTogglePriorityComplete(
                                        id,
                                        completed,
                                        true,
                                      )
                                  : undefined
                              }
                              onCarryForward={
                                effectiveEditable
                                  ? handleCarryForward
                                  : undefined
                              }
                              readOnly={!effectiveEditable}
                              workspaceId={
                                isViewingOthersSubmission ? orgId : undefined
                              }
                              currentMemberId={
                                isViewingOthersSubmission
                                  ? (currentMemberId ?? undefined)
                                  : undefined
                              }
                              commentCounts={priorityCommentCounts.value}
                              reactionsByPriority={initialPriorityReactions}
                              carriedIds={carriedFromIds.value}
                              highlightPriorityId={highlightPriorityId}
                              interactionsReadOnly={!!mimic}
                              emptyMessage={
                                isViewingOthersSubmission
                                  ? "No priorities set for this period"
                                  : "No priorities yet"
                              }
                            />
                          </PrioritySection>
                        )}

                        {(currentPriorities.value.length > 0 ||
                          effectiveEditable ||
                          hasPriorities) && (
                          <PrioritySection
                            variant="current"
                            title={
                              effectiveEditable
                                ? "Priorities for Next Week"
                                : isViewingOthersSubmission
                                  ? `${submission!.memberName}'s Current Priorities`
                                  : "Priorities"
                            }
                            description={
                              effectiveEditable
                                ? "What do you intend to accomplish before your next check-in?"
                                : isViewingOthersSubmission
                                  ? "What they're working on this period"
                                  : "Priorities set for this period"
                            }
                          >
                            <PriorityList
                              priorities={currentPriorities.value}
                              onToggleComplete={
                                effectiveEditable
                                  ? (id, completed) =>
                                      handleTogglePriorityComplete(
                                        id,
                                        completed,
                                        false,
                                      )
                                  : undefined
                              }
                              onUpdateTitle={
                                effectiveEditable
                                  ? handleUpdatePriorityTitle
                                  : undefined
                              }
                              onDelete={
                                effectiveEditable
                                  ? handleDeletePriority
                                  : undefined
                              }
                              onReorder={
                                effectiveEditable
                                  ? handleReorderPriorities
                                  : undefined
                              }
                              readOnly={!effectiveEditable}
                              workspaceId={
                                isViewingOthersSubmission ? orgId : undefined
                              }
                              currentMemberId={
                                isViewingOthersSubmission
                                  ? (currentMemberId ?? undefined)
                                  : undefined
                              }
                              commentCounts={priorityCommentCounts.value}
                              reactionsByPriority={initialPriorityReactions}
                              highlightPriorityId={highlightPriorityId}
                              interactionsReadOnly={!!mimic}
                              emptyMessage={
                                isViewingOthersSubmission
                                  ? "No priorities set for this period"
                                  : "No priorities yet"
                              }
                            />
                            {effectiveEditable && (
                              <PriorityInput
                                onAdd={handleAddPriority}
                                onTextChange={(text) => {
                                  pendingPriorityText.value = text;
                                }}
                              />
                            )}
                          </PrioritySection>
                        )}
                      </>
                    )}

                    {/* Rating questions */}
                    {ratingQuestions.length > 0 &&
                      (effectiveEditable ? (
                        <div class="flex flex-col gap-5">
                          {ratingQuestions.map((q) =>
                            renderQuestionCard(q, true),
                          )}
                        </div>
                      ) : (
                        <section>
                          {!effectiveEditable && (
                            <h2 class="text-2xl font-semibold text-text mb-6">
                              Questions
                            </h2>
                          )}
                          <div class="divide-y divide-border/60">
                            {ratingQuestions.map((q) =>
                              renderQuestionCard(q, false),
                            )}
                          </div>
                        </section>
                      ))}

                    {/* Values Recognition - after rating */}
                    {form.valuesRecognitionEnabled && coreValues.length > 0 && (
                      <RecognitionSection
                        editable={effectiveEditable}
                        recognitions={recognitionsList.value}
                        coreValues={coreValues}
                        orgId={orgId}
                        currentMemberId={currentMemberId}
                        commentCounts={recognitionCommentCounts.value}
                        reactions={recognitionReactions.value}
                        showForm={showRecognitionForm}
                        saving={recognitionSaving.value}
                        isMimicking={!!mimic}
                        highlightRecognitionId={highlightRecognitionId}
                        onAdd={handleCreateRecognition}
                        onRemove={handleRemoveRecognition}
                        onEdit={handleEditRecognition}
                        onToggleForm={() => {
                          showRecognitionForm.value =
                            !showRecognitionForm.value;
                        }}
                      />
                    )}

                    {/* Text/short answer questions */}
                    {textQuestions.length > 0 &&
                      (effectiveEditable ? (
                        <div class="flex flex-col gap-5">
                          {textQuestions.map((q) =>
                            renderQuestionCard(q, true),
                          )}
                        </div>
                      ) : (
                        <section>
                          <div class="divide-y divide-border/60">
                            {textQuestions.map((q) =>
                              renderQuestionCard(q, false),
                            )}
                          </div>
                        </section>
                      ))}
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Fixed footer for submitting */}
      {effectiveEditable && questions.length > 0 && (
        <div class="fixed bottom-0 left-0 right-0 bg-surface/80 backdrop-blur-lg border-t border-border/50 z-50">
          <div class="max-w-2xl mx-auto px-4 py-3 flex flex-col gap-2">
            {/* Progress bar */}
            {(() => {
              const answered = questions.filter((q) => {
                const a = answers.value[q.id];
                if (!a) return false;
                if (a.ratingAnswer != null) return true;
                if (a.textAnswer) {
                  const stripped = a.textAnswer
                    .replace(/<[^>]*>/g, "")
                    .replace(/&nbsp;/g, " ")
                    .trim();
                  return stripped.length > 0;
                }
                return false;
              }).length;
              const pct = Math.round((answered / questions.length) * 100);
              return (
                <div class="flex items-center gap-3">
                  <div class="flex-1 h-1 rounded-full bg-surface-muted overflow-hidden">
                    <div
                      class="h-full rounded-full bg-[var(--brand-500)] transition-all duration-500 ease-out"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span class="text-xs text-text-muted whitespace-nowrap">
                    {answered} of {questions.length}
                  </span>
                </div>
              );
            })()}
            <div class="flex justify-center">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting.value}
                class="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold shadow-sm bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {submitting.value && <IcoSpinner class="size-4 animate-spin" />}
                {submission?.submittedAt
                  ? "Update Response"
                  : "Submit Check-in"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed review footer - shown when viewing someone else's submission */}
      {submission?.canReview && (
        <div class="fixed bottom-0 left-0 right-0 bg-surface border-t border-border z-50">
          <div class="max-w-2xl mx-auto px-4 py-3">
            {allReviewedDone.value ? (
              // All done state
              <div class="flex flex-col items-center gap-2 py-2">
                <div class="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <IcoCheck class="size-5" />
                  <span class="text-base font-medium">All caught up!</span>
                </div>
                <a
                  href={`/orgs/${orgId}/team`}
                  class="text-sm text-green-700 dark:text-green-400 hover:underline font-medium"
                >
                  Back to Team
                </a>
              </div>
            ) : reviewedAt.value ? (
              // Already reviewed state
              <div class="flex items-center justify-center gap-4">
                <div class="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <IcoCheck class="size-4" />
                  <span class="text-sm font-medium">Reviewed</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMarkReviewed(false);
                  }}
                  disabled={markingReviewed.value}
                  class="text-sm text-text-muted hover:text-text font-medium transition-colors disabled:opacity-50"
                >
                  {markingReviewed.value ? "..." : "Undo"}
                </button>
              </div>
            ) : (
              // Needs review state - centered buttons
              <div class="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMarkReviewed(false);
                  }}
                  disabled={markingReviewed.value}
                  class="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-border text-text-muted hover:bg-surface-hover transition-colors disabled:opacity-50"
                >
                  {markingReviewed.value ? (
                    <IcoSpinner class="size-4 animate-spin" />
                  ) : (
                    <IcoCheck class="size-4" />
                  )}
                  Mark as Reviewed
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMarkReviewed(true);
                  }}
                  disabled={markingReviewed.value}
                  class="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50"
                >
                  {markingReviewed.value ? (
                    <IcoSpinner class="size-4 animate-spin" />
                  ) : (
                    <>
                      <IcoCheck class="size-4" />
                      Review & Next
                      <IcoChevronRight class="size-4" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </BrandedLayout>
  );
}

// ============================================================================
// Success Banner
// ============================================================================

function SuccessBanner() {
  return (
    <div class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-50 via-white to-sky-50 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 px-8 py-10 text-center border border-slate-200 dark:border-slate-700">
      {/* Confetti animation */}
      <Confetti duration={2500} particleCount={40} />

      {/* Decorative shapes - 15five style colorful accents */}
      {/* Top left - teal curved shape */}
      <div class="absolute -top-4 -left-4 size-20 rounded-full bg-teal-400/80 dark:bg-teal-500/60" />
      <div class="absolute top-8 left-12 size-6 rounded-full bg-amber-400/90 dark:bg-amber-500/70" />

      {/* Top right - pink curved shape */}
      <div class="absolute -top-6 right-8 w-16 h-24 rounded-full bg-pink-400/70 dark:bg-pink-500/50 rotate-45" />
      <div class="absolute top-2 right-2 size-8 rounded-full bg-sky-400/60 dark:bg-sky-500/50" />

      {/* Bottom left - orange circle */}
      <div class="absolute bottom-6 -left-2 size-10 rounded-full bg-orange-400/80 dark:bg-orange-500/60" />
      <div class="absolute bottom-2 left-10 size-4 rounded-full bg-amber-300/90 dark:bg-amber-400/70" />

      {/* Bottom right - blue/teal accent */}
      <div class="absolute -bottom-4 right-4 size-14 rounded-full bg-cyan-400/70 dark:bg-cyan-500/50" />
      <div class="absolute bottom-8 right-20 size-5 rounded-full bg-violet-400/60 dark:bg-violet-500/50" />

      {/* Main content */}
      <div class="relative z-10">
        {/* Celebration emoji */}
        <div class="text-5xl mb-3">🎉</div>

        {/* Headline */}
        <h2 class="text-2xl font-bold text-slate-800 dark:text-white">
          <span class="text-[var(--brand-600)] dark:text-[var(--brand-400)]">
            Woot Woot!
          </span>{" "}
          Check-in Submitted!
        </h2>

        <p class="mt-2 text-slate-600 dark:text-slate-300">
          Great job staying on track. Your manager will be notified.
        </p>

        {/* Stats row - placeholder for future streak feature */}
        <div class="mt-6 flex flex-wrap justify-center gap-6 text-sm">
          <div class="flex items-center gap-2 text-slate-700 dark:text-slate-200">
            <span class="text-lg">🔥</span>
            <span>
              <span class="font-semibold">Keep it up!</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Form Status Message
// ============================================================================

type StatusMessageResult = {
  text: string;
  type: "info" | "success" | "warning";
} | null;

function getFormStatusMessage(opts: {
  submission: SubmissionInfo | null;
  isEditable: boolean;
  viewingPastSubmission: boolean;
  isMimicking: boolean;
  mimicName: string | null;
}): StatusMessageResult {
  const {
    submission,
    isEditable,
    viewingPastSubmission,
    isMimicking,
    mimicName,
  } = opts;

  if (viewingPastSubmission && submission?.submittedAt) {
    const byText = submission.memberName ? ` by ${submission.memberName}` : "";
    return {
      text: `Submitted${byText} ${formatDateWithTime(submission.submittedAt)}`,
      type: "success",
    };
  }

  if (!isEditable && !viewingPastSubmission) {
    if (isMimicking) {
      return {
        text: `Viewing as ${mimicName ?? "another user"} (read-only)`,
        type: "info",
      };
    }
    return {
      text: "This form's submission period has ended",
      type: "warning",
    };
  }

  if (submission?.submittedAt && isEditable) {
    return {
      text: `Submitted ${formatDateWithTime(submission.submittedAt)} - You can update your response`,
      type: "success",
    };
  }

  return null;
}

// ============================================================================
// Question Card
// ============================================================================

type QuestionCardProps = {
  question: QuestionInfo;
  answer: FormAnswer | null;
  onAnswer: (answer: FormAnswer) => void;
  disabled?: boolean;
  /** Workspace ID for comments/reactions (optional) */
  workspaceId?: string;
  /** Current user's member ID for comments (optional) */
  currentMemberId?: string;
  /** Comment count for this answer */
  commentCount?: number;
  /** Pre-loaded reactions for this answer (skips individual fetch) */
  initialReactions?: ReactionSummary[];
  /** Whether user is in mimic mode (read-only for reactions/comments) */
  isMimicking?: boolean;
  /** Extra answers for multi-answer questions */
  extraAnswers?: FormAnswer[];
  /** Reaction data for extra answers (keyed by answer ID) */
  extraAnswerReactions?: Record<string, ReactionSummary[]>;
  /** Callbacks for managing extra answers (editable mode only) */
  onAddExtraAnswer?: () => void;
  onUpdateExtraAnswer?: (index: number, answer: FormAnswer) => void;
  onRemoveExtraAnswer?: (index: number) => void;
  /** Rating trend data (sparkline) for read-only view */
  ratingTrend?: RatingTrendPoint[];
  /** Auto-expand comment thread and scroll to this card (for notification links) */
  autoExpandComments?: boolean;
};

function QuestionCard({
  question,
  answer,
  onAnswer,
  disabled,
  workspaceId,
  currentMemberId,
  commentCount: initialCommentCount = 0,
  initialReactions,
  isMimicking = false,
  extraAnswers = [],
  extraAnswerReactions = {},
  onAddExtraAnswer,
  onUpdateExtraAnswer,
  onRemoveExtraAnswer,
  ratingTrend,
  autoExpandComments = false,
}: QuestionCardProps) {
  const localCommentCount = useSignal(initialCommentCount);
  const commentsOpen = useSignal(autoExpandComments);
  const cardRef = useRef<HTMLElement>(null);

  // Sync with prop when it changes (e.g., from batch fetch)
  useEffect(() => {
    localCommentCount.value = initialCommentCount;
  }, [initialCommentCount]);

  // Auto-expand and scroll when arriving from notification link
  useEffect(() => {
    if (autoExpandComments) {
      commentsOpen.value = true;
      setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 300);
    }
  }, [autoExpandComments]);

  // Comments are only available on submitted answers with an ID
  const hasCommentsFeature =
    disabled && answer?.id && workspaceId && currentMemberId;

  const handleTextChange = (html: string) => {
    onAnswer({
      questionId: question.id,
      textAnswer: html,
      ratingAnswer: null,
    });
  };

  const handleRatingChange = (value: number) => {
    onAnswer({
      questionId: question.id,
      textAnswer: null,
      ratingAnswer: value,
      comment: answer?.comment ?? null,
    });
  };

  const handleCommentChange = (text: string) => {
    onAnswer({
      questionId: question.id,
      textAnswer: answer?.textAnswer ?? null,
      ratingAnswer: answer?.ratingAnswer ?? null,
      comment: text || null,
    });
  };

  // Read-only mode: 15five-inspired layout
  if (disabled) {
    return (
      <article ref={cardRef} class="group">
        {/* Question - small label anchoring the answer */}
        <h3
          class="text-sm font-semibold text-text-muted mb-2 prose prose-sm max-w-none [&_p]:m-0 [&_p]:text-sm [&_p]:font-semibold [&_p]:text-text-muted"
          dangerouslySetInnerHTML={{ __html: question.title }}
        />
        {question.description && (
          <div
            class="text-sm text-text-muted mb-4 leading-relaxed prose prose-sm max-w-none [&_p]:m-0 [&_a]:text-[var(--brand-600)] [&_a]:underline"
            dangerouslySetInnerHTML={{
              __html: linkifyHtml(question.description),
            }}
          />
        )}

        {/* Answer with bullet marker */}
        {question.type === "short_answer" ? (
          answer?.textAnswer ? (
            <div class="flex flex-col gap-3">
              <div class="flex gap-3">
                {/* Colored bullet */}
                <div class="flex-shrink-0 mt-2">
                  <div class="size-2 rounded-full bg-[var(--brand-500)]" />
                </div>
                {/* Answer content */}
                <div class="flex-1 min-w-0">
                  <div
                    class="prose prose-base max-w-none text-text [&_p]:m-0 [&_p]:leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: decodeMentionsToHtml(answer.textAnswer),
                    }}
                  />
                  {/* Reaction + Comment below answer */}
                  {hasCommentsFeature && (
                    <div class="mt-2 flex items-center gap-3">
                      <ReactionBar
                        workspaceId={workspaceId!}
                        targetType="form_answer"
                        targetId={answer!.id!}
                        initialReactions={initialReactions}
                        readOnly={isMimicking}
                      />
                      <CommentButton
                        count={localCommentCount.value}
                        isOpen={commentsOpen.value}
                        onClick={() => {
                          commentsOpen.value = !commentsOpen.value;
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              {/* Extra answers in read-only mode */}
              {extraAnswers.map(
                (extra) =>
                  extra.textAnswer && (
                    <div key={extra.id} class="flex gap-3">
                      <div class="flex-shrink-0 mt-2">
                        <div class="size-2 rounded-full bg-[var(--brand-500)]" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div
                          class="prose prose-base max-w-none text-text [&_p]:m-0 [&_p]:leading-relaxed"
                          dangerouslySetInnerHTML={{
                            __html: decodeMentionsToHtml(extra.textAnswer),
                          }}
                        />
                        {hasCommentsFeature && extra.id && (
                          <div class="mt-2">
                            <ReactionBar
                              workspaceId={workspaceId!}
                              targetType="form_answer"
                              targetId={extra.id}
                              initialReactions={extraAnswerReactions[extra.id]}
                              readOnly={isMimicking}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ),
              )}
            </div>
          ) : (
            <div class="flex gap-3">
              <div class="flex-shrink-0 mt-2">
                <div class="size-2 rounded-full bg-surface-muted" />
              </div>
              <p class="text-text-muted italic">No response</p>
            </div>
          )
        ) : answer?.ratingAnswer != null ? (
          <div>
            <RatingTrendChart
              data={
                ratingTrend && ratingTrend.length > 0
                  ? ratingTrend
                  : [
                      {
                        periodStart: new Date().toISOString(),
                        rating: answer.ratingAnswer,
                      },
                    ]
              }
              max={question.ratingMax}
            />
            {/* User's note/comment on the rating */}
            {answer?.comment && (
              <div class="mt-3 flex gap-2">
                <div class="flex-shrink-0 mt-1.5">
                  <div class="size-1.5 rounded-full bg-text-subtle" />
                </div>
                <p class="text-sm text-text-muted italic">{answer.comment}</p>
              </div>
            )}
            {/* Reaction + Comment on same row */}
            {hasCommentsFeature && (
              <div class="mt-3 flex items-center gap-3">
                <ReactionBar
                  workspaceId={workspaceId!}
                  targetType="form_answer"
                  targetId={answer!.id!}
                  initialReactions={initialReactions}
                  readOnly={isMimicking}
                />
                <CommentButton
                  count={localCommentCount.value}
                  isOpen={commentsOpen.value}
                  onClick={() => {
                    commentsOpen.value = !commentsOpen.value;
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div class="flex gap-3">
            <div class="flex-shrink-0 mt-2">
              <div class="size-2 rounded-full bg-surface-muted" />
            </div>
            <p class="text-text-muted italic">No response</p>
          </div>
        )}

        {/* Comment thread: click-to-expand */}
        {hasCommentsFeature && commentsOpen.value && (
          <div class="mt-2 ml-5 rounded-lg bg-surface-muted/50 dark:bg-surface-muted/30">
            <div class="p-4">
              <CommentThread
                workspaceId={workspaceId!}
                targetType="form_answer"
                targetId={answer!.id!}
                currentMemberId={currentMemberId!}
                readOnly={isMimicking}
                onCommentCountChange={(count) => {
                  localCommentCount.value = count;
                }}
              />
            </div>
          </div>
        )}
      </article>
    );
  }

  // Editable mode: simpler layout focused on input
  return (
    <div class="rounded-2xl bg-surface shadow-sm ring-1 ring-black/[0.04] p-6">
      {/* Question */}
      <div class="flex items-start gap-1 mb-2">
        <h3
          class="text-[15px] font-semibold leading-snug text-text prose prose-sm max-w-none [&_p]:m-0"
          dangerouslySetInnerHTML={{ __html: question.title }}
        />
        {!question.required && (
          <span class="text-xs text-text-subtle font-normal flex-shrink-0">
            (optional)
          </span>
        )}
      </div>
      {question.description && (
        <div
          class="text-sm text-text-muted mb-4 leading-relaxed prose prose-sm max-w-none [&_p]:m-0 [&_a]:text-[var(--brand-600)] [&_a]:underline"
          dangerouslySetInnerHTML={{
            __html: linkifyHtml(question.description),
          }}
        />
      )}
      {!question.description && <div class="mb-2" />}

      {/* Answer input */}
      {question.type === "short_answer" ? (
        <div class="flex flex-col gap-3">
          <div class="rounded-lg border border-border transition-colors focus-within:border-[var(--brand-500)] focus-within:ring-2 focus-within:ring-[var(--brand-500)]/20">
            <MentionRichTextEditor
              value={answer?.textAnswer ?? ""}
              onChange={handleTextChange}
              workspaceId={workspaceId!}
              placeholder="Type your answer... (use @ to mention)"
            />
          </div>

          {/* Extra answers for multi-answer questions */}
          {extraAnswers.map((extra, i) => (
            <div key={extra.id ?? i} class="relative group/extra">
              <div class="rounded-lg border border-border transition-colors focus-within:border-[var(--brand-500)] focus-within:ring-2 focus-within:ring-[var(--brand-500)]/20">
                <MentionRichTextEditor
                  value={extra.textAnswer ?? ""}
                  onChange={(html: string) =>
                    onUpdateExtraAnswer?.(i, {
                      questionId: question.id,
                      textAnswer: html,
                      ratingAnswer: null,
                    })
                  }
                  workspaceId={workspaceId!}
                  placeholder="Type your answer... (use @ to mention)"
                />
              </div>
              <button
                type="button"
                onClick={() => onRemoveExtraAnswer?.(i)}
                class="absolute -top-2 -right-2 size-6 rounded-full bg-surface border border-border shadow-sm flex items-center justify-center text-text-muted hover:text-danger hover:border-danger/30 transition-colors opacity-0 group-hover/extra:opacity-100 focus:opacity-100"
                title="Remove this answer"
              >
                <IcoX class="size-3.5" />
              </button>
            </div>
          ))}

          {/* Add another answer button */}
          {question.allowMultiple && onAddExtraAnswer && (
            <button
              type="button"
              onClick={onAddExtraAnswer}
              class="flex items-center gap-1.5 text-sm text-[var(--brand-600)] hover:text-[var(--brand-700)] font-medium transition-colors self-start"
            >
              <IcoPlus class="size-4" />
              Add another response
            </button>
          )}
        </div>
      ) : (
        <div class="flex flex-col gap-3">
          <RatingInput
            max={question.ratingMax}
            value={answer?.ratingAnswer ?? null}
            onChange={handleRatingChange}
            disabled={false}
          />
          {/* Optional comment for rating */}
          {answer?.ratingAnswer && (
            <div class="mt-1">
              <label class="block text-sm text-text-muted mb-1.5">
                Add a note <span class="text-text-subtle">(optional)</span>
              </label>
              <textarea
                value={answer?.comment ?? ""}
                onInput={(e) =>
                  handleCommentChange((e.target as HTMLTextAreaElement).value)
                }
                placeholder="Why did you choose this rating?"
                class="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text text-sm placeholder:text-text-subtle resize-none focus:outline-none focus:border-[var(--brand-500)] focus:ring-1 focus:ring-[var(--brand-500)]"
                rows={2}
                maxLength={2000}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Recognition Section
// ============================================================================

type RecognitionSectionProps = {
  editable: boolean;
  recognitions: ValueRecognition[];
  coreValues: CoreValue[];
  orgId: string;
  currentMemberId: string | null;
  commentCounts: Record<string, number>;
  reactions: Record<string, ReactionSummary[]>;
  showForm: { value: boolean };
  saving: boolean;
  isMimicking: boolean;
  highlightRecognitionId?: string;
  onAdd: (
    recipientMemberIds: string[],
    valueIds: string[],
    message: string,
  ) => void;
  onRemove: (id: string) => void;
  onEdit: (
    id: string,
    recipientMemberIds: string[],
    valueIds: string[],
    message: string,
  ) => Promise<void>;
  onToggleForm: () => void;
};

function RecognitionSection({
  editable,
  recognitions,
  coreValues,
  orgId,
  currentMemberId,
  commentCounts,
  reactions: reactionsByTarget,
  showForm,
  saving,
  isMimicking,
  highlightRecognitionId,
  onAdd,
  onRemove,
  onEdit,
  onToggleForm,
}: RecognitionSectionProps) {
  // Form state for new recognition
  const selectedRecipients = useSignal<{ memberId: string; name: string }[]>(
    [],
  );
  const selectedValues = useSignal<string[]>([]);
  const message = useSignal("");
  const searchQuery = useSignal("");
  // AI coaching state
  const aiSuggestion = useSignal<string | null>(null);
  const aiEvaluating = useSignal(false);
  const memberResults = useSignal<
    { memberId: string; name: string; profilePictureUrl: string | null }[]
  >([]);
  const searching = useSignal(false);
  const highlightedIdx = useSignal(-1);

  const handleSearch = async (query: string) => {
    searchQuery.value = query;
    // Strip leading @ for consistency with @mention UX in other fields
    const cleanQuery = query.replace(/^@/, "");
    const minLen = query.startsWith("@") ? 1 : 2;
    if (cleanQuery.length < minLen) {
      memberResults.value = [];
      highlightedIdx.value = -1;
      return;
    }
    searching.value = true;
    try {
      const result = await rpc.members.searchForMention({
        workspaceId: orgId,
        query: cleanQuery,
      });
      const selectedIds = selectedRecipients.value.map((r) => r.memberId);
      memberResults.value = (result.members ?? []).filter(
        (m) => !selectedIds.includes(m.memberId),
      );
      highlightedIdx.value = memberResults.value.length > 0 ? 0 : -1;
    } catch {
      memberResults.value = [];
    } finally {
      searching.value = false;
    }
  };

  const handleAddRecipient = (member: { memberId: string; name: string }) => {
    if (!selectedRecipients.value.some((r) => r.memberId === member.memberId)) {
      selectedRecipients.value = [
        ...selectedRecipients.value,
        { memberId: member.memberId, name: member.name },
      ];
    }
    searchQuery.value = "";
    memberResults.value = [];
  };

  const handleRemoveRecipient = (memberId: string) => {
    selectedRecipients.value = selectedRecipients.value.filter(
      (r) => r.memberId !== memberId,
    );
  };

  const handleToggleValue = (valueId: string) => {
    if (selectedValues.value.includes(valueId)) {
      selectedValues.value = selectedValues.value.filter(
        (id) => id !== valueId,
      );
    } else {
      selectedValues.value = [...selectedValues.value, valueId];
    }
  };

  const doSave = () => {
    onAdd(
      selectedRecipients.value.map((r) => r.memberId),
      selectedValues.value,
      message.value,
    );
    selectedRecipients.value = [];
    selectedValues.value = [];
    message.value = "";
    aiSuggestion.value = null;
  };

  const handleSave = async () => {
    if (
      selectedRecipients.value.length === 0 ||
      selectedValues.value.length === 0 ||
      !message.value.trim()
    )
      return;

    // Evaluate with AI before saving
    aiEvaluating.value = true;
    aiSuggestion.value = null;
    try {
      const result = await rpc.valueRecognitions.evaluateQuality({
        recognitions: [
          {
            id: "new",
            message: message.value,
            recipientNames: selectedRecipients.value.map((r) => r.name),
            valueNames: coreValues
              .filter((cv) => selectedValues.value.includes(cv.id))
              .map((cv) => cv.name),
          },
        ],
        totalValues: coreValues.length,
      });
      const evaluation = result.results[0];
      if (evaluation?.quality === "could_improve" && evaluation.suggestion) {
        aiSuggestion.value = evaluation.suggestion;
        return; // Don't save yet — show suggestion
      }
    } catch {
      // AI failed — proceed with save
    } finally {
      aiEvaluating.value = false;
    }

    doSave();
  };

  // Hide the entire section in read-only mode if there are no recognitions
  if (!editable && recognitions.length === 0) return null;

  return (
    <section class="space-y-4">
      <div>
        <h2 class="text-2xl font-semibold text-text">Values Recognition</h2>
        <p class="text-sm text-text-muted mt-1">
          {
            "Please share 1-2 examples from the last week of someone exhibiting/exemplifying ONE of our "
          }
          <a
            href="https://drive.google.com/file/d/1SBMj5oQf7HaEGhOtKCcwjPdV5PwMbMNm/view"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[var(--brand-600)] hover:underline"
          >
            core values
          </a>
          {"."}
        </p>
      </div>

      {/* Saved recognitions */}
      {recognitions.map((rec) => (
        <RecognitionCard
          key={rec.id}
          recognition={rec}
          orgId={orgId}
          currentMemberId={currentMemberId}
          coreValues={coreValues}
          commentCount={commentCounts[rec.id] ?? 0}
          initialReactions={reactionsByTarget[rec.id]}
          isMimicking={isMimicking}
          editable={editable}
          autoExpandComments={highlightRecognitionId === rec.id}
          onRemove={() => onRemove(rec.id)}
          onEdit={onEdit}
        />
      ))}

      {/* Add recognition form */}
      {editable &&
        !isMimicking &&
        (showForm.value ? (
          <div class="rounded-xl border border-border bg-surface p-4 space-y-4">
            {/* Recipient search */}
            <div>
              <label class="block text-sm font-medium text-text mb-1">
                Who are you recognizing?
              </label>
              {selectedRecipients.value.length > 0 && (
                <div class="flex flex-wrap gap-1.5 mb-2">
                  {selectedRecipients.value.map((r) => (
                    <span
                      key={r.memberId}
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--brand-100)] text-[var(--brand-700)]"
                    >
                      {r.name}
                      <button
                        type="button"
                        onClick={() => handleRemoveRecipient(r.memberId)}
                        class="hover:text-[var(--brand-900)]"
                      >
                        <IcoX class="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div class="relative">
                <IcoSearch class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-subtle" />
                <input
                  type="text"
                  value={searchQuery.value}
                  onInput={(e) =>
                    handleSearch((e.target as HTMLInputElement).value)
                  }
                  onKeyDown={(e) => {
                    const results = memberResults.value;
                    if (!results.length) return;
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      highlightedIdx.value =
                        (highlightedIdx.value + 1) % results.length;
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      highlightedIdx.value =
                        (highlightedIdx.value - 1 + results.length) %
                        results.length;
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      const idx = highlightedIdx.value;
                      const selected = results[idx];
                      if (selected) {
                        handleAddRecipient({
                          memberId: selected.memberId,
                          name: selected.name,
                        });
                        highlightedIdx.value = -1;
                      }
                    } else if (e.key === "Escape") {
                      memberResults.value = [];
                      highlightedIdx.value = -1;
                    }
                  }}
                  placeholder="Search for a teammate..."
                  class="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-surface-page focus:outline-none focus:ring-2 focus:ring-[var(--brand-500)]/30 focus:border-[var(--brand-500)]"
                />
              </div>
              {memberResults.value.length > 0 && (
                <div class="mt-1 border border-border rounded-lg bg-surface shadow-lg max-h-40 overflow-y-auto">
                  {memberResults.value.map((m, i) => (
                    <button
                      key={m.memberId}
                      type="button"
                      onClick={() =>
                        handleAddRecipient({
                          memberId: m.memberId,
                          name: m.name,
                        })
                      }
                      class={`w-full text-left px-3 py-2 text-sm ${i === highlightedIdx.value ? "bg-surface-hover" : "hover:bg-surface-hover"}`}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Value chips */}
            <div>
              <label class="block text-sm font-medium text-text mb-1">
                For which values?
              </label>
              <div class="flex flex-wrap gap-2">
                {coreValues.map((cv) => {
                  const selected = selectedValues.value.includes(cv.id);
                  return (
                    <ValueChip
                      key={cv.id}
                      value={cv}
                      selected={selected}
                      onClick={() => handleToggleValue(cv.id)}
                    />
                  );
                })}
              </div>
            </div>

            {/* Message */}
            <div>
              <label class="block text-sm font-medium text-text mb-1">
                {"Format: SBI = Situation > Behavior > Impact"}
              </label>
              <textarea
                value={message.value}
                onInput={(e) => {
                  message.value = (e.target as HTMLTextAreaElement).value;
                  // Clear AI suggestion when user edits
                  if (aiSuggestion.value) {
                    aiSuggestion.value = null;
                  }
                }}
                placeholder="Describe the specific situation and what they did..."
                rows={3}
                maxLength={2000}
                class="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface focus:outline-none focus:ring-2 focus:ring-[var(--brand-500)]/30 focus:border-[var(--brand-500)] resize-none"
              />
            </div>

            {/* AI coaching suggestion */}
            {aiSuggestion.value && (
              <div class="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 p-3 text-sm">
                <div class="flex gap-2">
                  <span class="text-base leading-none flex-shrink-0 mt-0.5">
                    ✨
                  </span>
                  <div class="flex-1 min-w-0">
                    <p class="text-indigo-800 dark:text-indigo-200">
                      {aiSuggestion.value}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        aiSuggestion.value = null;
                        doSave();
                      }}
                      class="mt-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Save as is
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div class="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={
                  saving ||
                  aiEvaluating.value ||
                  selectedRecipients.value.length === 0 ||
                  selectedValues.value.length === 0 ||
                  !message.value.trim()
                }
                class="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {aiEvaluating.value
                  ? "Checking..."
                  : saving
                    ? "Saving..."
                    : "Save Recognition"}
              </button>
              <button
                type="button"
                onClick={onToggleForm}
                class="px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onToggleForm}
            class={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-medium transition-colors ${
              recognitions.length === 0
                ? "bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white text-sm"
                : "border border-dashed border-border bg-surface-hover/50 hover:bg-surface-hover hover:border-text-subtle text-text-muted hover:text-text text-sm"
            }`}
          >
            <IcoPlus class="size-4" />
            Recognize a Teammate
          </button>
        ))}
    </section>
  );
}

// ============================================================================
// Recognition Card
// ============================================================================

type RecognitionCardProps = {
  recognition: ValueRecognition;
  orgId: string;
  currentMemberId: string | null;
  coreValues: CoreValue[];
  commentCount: number;
  initialReactions?: ReactionSummary[];
  isMimicking: boolean;
  editable: boolean;
  autoExpandComments?: boolean;
  onRemove: () => void;
  onEdit: (
    id: string,
    recipientMemberIds: string[],
    valueIds: string[],
    message: string,
  ) => Promise<void>;
};

function RecognitionCard({
  recognition,
  orgId,
  currentMemberId,
  coreValues,
  commentCount: initialCommentCount,
  initialReactions,
  isMimicking,
  editable,
  autoExpandComments,
  onRemove,
  onEdit,
}: RecognitionCardProps) {
  const showComments = useSignal(autoExpandComments ?? false);
  const localCommentCount = useSignal(initialCommentCount);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localCommentCount.value = initialCommentCount;
  }, [initialCommentCount]);

  const highlighted = useSignal(false);

  // Auto-expand and scroll when arriving from notification link
  useEffect(() => {
    if (autoExpandComments) {
      showComments.value = true;
      const timer = setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        highlighted.value = true;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [autoExpandComments]);

  const isAuthor = currentMemberId === recognition.author.memberId;

  // Edit state
  const isEditing = useSignal(false);
  const editRecipients = useSignal<{ memberId: string; name: string }[]>([]);
  const editValues = useSignal<string[]>([]);
  const editMessage = useSignal("");
  const editSearchQuery = useSignal("");
  const editMemberResults = useSignal<
    { memberId: string; name: string; profilePictureUrl: string | null }[]
  >([]);
  const editSaving = useSignal(false);
  // AI coaching for edit mode
  const editAiSuggestion = useSignal<string | null>(null);
  const editAiEvaluating = useSignal(false);

  const startEditing = () => {
    editRecipients.value = recognition.recipients.map((r) => ({
      memberId: r.memberId,
      name: r.name,
    }));
    editValues.value = recognition.values.map((v) => v.id);
    editMessage.value = recognition.message;
    editSearchQuery.value = "";
    editMemberResults.value = [];
    isEditing.value = true;
  };

  const cancelEditing = () => {
    isEditing.value = false;
  };

  const handleEditSearch = async (query: string) => {
    editSearchQuery.value = query;
    const cleanQuery = query.replace(/^@/, "");
    const minLen = query.startsWith("@") ? 1 : 2;
    if (cleanQuery.length < minLen) {
      editMemberResults.value = [];
      return;
    }
    try {
      const result = await rpc.members.searchForMention({
        workspaceId: orgId,
        query: cleanQuery,
      });
      const selectedIds = editRecipients.value.map((r) => r.memberId);
      editMemberResults.value = (result.members ?? []).filter(
        (m) => !selectedIds.includes(m.memberId),
      );
    } catch {
      editMemberResults.value = [];
    }
  };

  const doEditSave = async () => {
    editSaving.value = true;
    try {
      await onEdit(
        recognition.id,
        editRecipients.value.map((r) => r.memberId),
        editValues.value,
        editMessage.value,
      );
      isEditing.value = false;
      editAiSuggestion.value = null;
    } catch {
      // Error already shown by parent
    } finally {
      editSaving.value = false;
    }
  };

  const handleEditSave = async () => {
    if (
      editRecipients.value.length === 0 ||
      editValues.value.length === 0 ||
      !editMessage.value.trim()
    )
      return;

    editAiEvaluating.value = true;
    editAiSuggestion.value = null;
    try {
      const result = await rpc.valueRecognitions.evaluateQuality({
        recognitions: [
          {
            id: recognition.id,
            message: editMessage.value,
            recipientNames: editRecipients.value.map((r) => r.name),
            valueNames: coreValues
              .filter((cv) => editValues.value.includes(cv.id))
              .map((cv) => cv.name),
          },
        ],
        totalValues: coreValues.length,
      });
      const evaluation = result.results[0];
      if (evaluation?.quality === "could_improve" && evaluation.suggestion) {
        editAiSuggestion.value = evaluation.suggestion;
        return;
      }
    } catch {
      // AI failed — proceed
    } finally {
      editAiEvaluating.value = false;
    }

    await doEditSave();
  };

  if (isEditing.value) {
    return (
      <div class="rounded-xl border border-border bg-surface p-4 space-y-4">
        {/* Recipient search */}
        <div>
          <label class="block text-sm font-medium text-text mb-1">
            Who are you recognizing?
          </label>
          {editRecipients.value.length > 0 && (
            <div class="flex flex-wrap gap-1.5 mb-2">
              {editRecipients.value.map((r) => (
                <span
                  key={r.memberId}
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--brand-100)] text-[var(--brand-700)]"
                >
                  {r.name}
                  <button
                    type="button"
                    onClick={() => {
                      editRecipients.value = editRecipients.value.filter(
                        (x) => x.memberId !== r.memberId,
                      );
                    }}
                    class="hover:text-[var(--brand-900)]"
                  >
                    <IcoX class="size-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div class="relative">
            <IcoSearch class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-subtle" />
            <input
              type="text"
              value={editSearchQuery.value}
              onInput={(e) =>
                handleEditSearch((e.target as HTMLInputElement).value)
              }
              placeholder="Search for a teammate..."
              class="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-surface-page focus:outline-none focus:ring-2 focus:ring-[var(--brand-500)]/30 focus:border-[var(--brand-500)]"
            />
          </div>
          {editMemberResults.value.length > 0 && (
            <div class="mt-1 border border-border rounded-lg bg-surface shadow-lg max-h-40 overflow-y-auto">
              {editMemberResults.value.map((m) => (
                <button
                  key={m.memberId}
                  type="button"
                  onClick={() => {
                    if (
                      !editRecipients.value.some(
                        (r) => r.memberId === m.memberId,
                      )
                    ) {
                      editRecipients.value = [
                        ...editRecipients.value,
                        { memberId: m.memberId, name: m.name },
                      ];
                    }
                    editSearchQuery.value = "";
                    editMemberResults.value = [];
                  }}
                  class="w-full text-left px-3 py-2 text-sm hover:bg-surface-hover"
                >
                  {m.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Value chips */}
        <div>
          <label class="block text-sm font-medium text-text mb-1">
            For which values?
          </label>
          <div class="flex flex-wrap gap-2">
            {coreValues.map((cv) => {
              const selected = editValues.value.includes(cv.id);
              return (
                <ValueChip
                  key={cv.id}
                  value={cv}
                  selected={selected}
                  onClick={() => {
                    if (selected) {
                      editValues.value = editValues.value.filter(
                        (id) => id !== cv.id,
                      );
                    } else {
                      editValues.value = [...editValues.value, cv.id];
                    }
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Message */}
        <div>
          <label class="block text-sm font-medium text-text mb-1">
            {"Format: SBI = Situation > Behavior > Impact"}
          </label>
          <textarea
            value={editMessage.value}
            onInput={(e) => {
              editMessage.value = (e.target as HTMLTextAreaElement).value;
              if (editAiSuggestion.value) {
                editAiSuggestion.value = null;
              }
            }}
            placeholder="Describe the specific situation and what they did..."
            rows={3}
            maxLength={2000}
            class="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface focus:outline-none focus:ring-2 focus:ring-[var(--brand-500)]/30 focus:border-[var(--brand-500)] resize-none"
          />
        </div>

        {/* AI coaching suggestion */}
        {editAiSuggestion.value && (
          <div class="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 p-3 text-sm">
            <div class="flex gap-2">
              <span class="text-base leading-none flex-shrink-0 mt-0.5">
                ✨
              </span>
              <div class="flex-1 min-w-0">
                <p class="text-indigo-800 dark:text-indigo-200">
                  {editAiSuggestion.value}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    editAiSuggestion.value = null;
                    doEditSave();
                  }}
                  class="mt-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Save as is
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={handleEditSave}
            disabled={
              editSaving.value ||
              editAiEvaluating.value ||
              editRecipients.value.length === 0 ||
              editValues.value.length === 0 ||
              !editMessage.value.trim()
            }
            class="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {editAiEvaluating.value
              ? "Checking..."
              : editSaving.value
                ? "Saving..."
                : "Save"}
          </button>
          <button
            type="button"
            onClick={cancelEditing}
            class="px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      class="rounded-xl border border-border bg-surface"
      style={
        highlighted.value
          ? { animation: "highlight-fade 2s ease-out forwards" }
          : undefined
      }
    >
      <div class="p-4 space-y-3">
        {/* Recipients and values */}
        <div class="flex items-start justify-between gap-2">
          <div class="space-y-1.5">
            <div class="flex flex-wrap items-center gap-1.5 text-sm">
              {recognition.author.profilePictureUrl ? (
                <img
                  src={recognition.author.profilePictureUrl}
                  alt=""
                  class="size-5 rounded-full object-cover"
                />
              ) : (
                <span class="size-5 rounded-full bg-surface-muted flex items-center justify-center text-[10px] font-semibold text-text-muted">
                  {recognition.author.name?.charAt(0).toUpperCase()}
                </span>
              )}
              <span class="font-medium text-text">
                {recognition.author.name}
              </span>
              <span class="text-text-muted">recognized</span>
              {recognition.recipients.map((r) => (
                <span
                  key={r.memberId}
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-surface-hover text-text"
                >
                  {r.profilePictureUrl ? (
                    <img
                      src={r.profilePictureUrl}
                      alt=""
                      class="size-4 rounded-full object-cover"
                    />
                  ) : (
                    <span class="size-4 rounded-full bg-surface-muted flex items-center justify-center text-[9px] font-semibold text-text-muted">
                      {r.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                  {r.name}
                </span>
              ))}
            </div>
            <div class="flex flex-wrap gap-1.5">
              {recognition.values.map((v) => {
                const cv = coreValues.find((c) => c.id === v.id);
                return (
                  <ValuePill
                    key={v.id}
                    name={v.name}
                    tagline={cv?.tagline}
                    description={cv?.description}
                  />
                );
              })}
            </div>
          </div>
          {editable && isAuthor && (
            <div class="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={startEditing}
                class="text-text-subtle hover:text-text transition-colors p-0.5"
              >
                <IcoPencil class="size-4" />
              </button>
              <button
                type="button"
                onClick={onRemove}
                class="text-text-subtle hover:text-red-500 transition-colors p-0.5"
              >
                <IcoX class="size-4" />
              </button>
            </div>
          )}
        </div>

        {/* Message */}
        {recognition.message && (
          <p class="text-sm text-text whitespace-pre-wrap">
            {recognition.message}
          </p>
        )}
      </div>

      {/* Reactions and comments bar */}
      {orgId && currentMemberId && (
        <div class="px-4 pb-3 flex items-center gap-3">
          <ReactionBar
            workspaceId={orgId}
            targetType="value_recognition"
            targetId={recognition.id}
            initialReactions={initialReactions}
            readOnly={isMimicking}
          />
          <CommentButton
            count={localCommentCount.value}
            isOpen={showComments.value}
            onClick={() => {
              showComments.value = !showComments.value;
            }}
          />
        </div>
      )}

      {/* Comment thread */}
      {showComments.value && orgId && currentMemberId && (
        <div class="border-t border-border px-4 py-3">
          <CommentThread
            workspaceId={orgId}
            targetType="value_recognition"
            targetId={recognition.id}
            currentMemberId={currentMemberId}
            onCommentCountChange={(count) => {
              localCommentCount.value = count;
            }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Value Chip (interactive, for create/edit forms)
// ============================================================================

function ValueChip({
  value,
  selected,
  onClick,
}: {
  value: CoreValue;
  selected: boolean;
  onClick: () => void;
}) {
  const hasDetail = !!(value.tagline || value.description);

  return (
    <div class="relative group">
      <button
        type="button"
        onClick={onClick}
        class={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          selected
            ? "bg-[var(--brand-600)] text-white"
            : "bg-surface-hover text-text-muted hover:text-text"
        }`}
      >
        {value.name}
      </button>
      {hasDetail && (
        <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 pointer-events-none z-50">
          <div class="bg-surface border border-border rounded-lg shadow-lg p-3 text-left">
            {value.tagline && (
              <p class="text-xs font-semibold text-text leading-snug">
                {value.tagline}
              </p>
            )}
            {value.description && (
              <p
                class={`text-xs text-text-muted leading-relaxed ${value.tagline ? "mt-1" : ""}`}
              >
                {value.description}
              </p>
            )}
          </div>
          <div class="flex justify-center">
            <div class="w-2 h-2 bg-surface border-b border-r border-border rotate-45 -mt-[5px]" />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Value Pill (read-only, for recognition cards)
// ============================================================================

function ValuePill({
  name,
  tagline,
  description,
}: {
  name: string;
  tagline?: string;
  description?: string;
}) {
  const hasDetail = !!(tagline || description);

  return (
    <div class="relative group">
      <span class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--brand-100)] text-[var(--brand-700)] cursor-default">
        {name}
      </span>
      {hasDetail && (
        <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 pointer-events-none z-50">
          <div class="bg-surface border border-border rounded-lg shadow-lg p-3 text-left">
            {tagline && (
              <p class="text-xs font-semibold text-text leading-snug">
                {tagline}
              </p>
            )}
            {description && (
              <p
                class={`text-xs text-text-muted leading-relaxed ${tagline ? "mt-1" : ""}`}
              >
                {description}
              </p>
            )}
          </div>
          <div class="flex justify-center">
            <div class="w-2 h-2 bg-surface border-b border-r border-border rotate-45 -mt-[5px]" />
          </div>
        </div>
      )}
    </div>
  );
}
